import {ReactNode, useCallback, useEffect, useState} from 'react';
import Swiper from 'swiper';
import {requestContext} from '../../../../../../common/requestContext';
import {useWindowInnerHeight} from '../../../../../../common/shared/hooks/useWindowInnerHeight';
import {preventDefaultEvent} from '../../../../../../common/shared/utils/preventDefaultEvent.function';
import {twMerge} from '../../../../../../common/twMerge';
import useTypedDispatch from '../../../../../../common/useTypedDispatch';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {Image} from '../../../../../../design-system/atoms/Image/Image';
import {ViewerSlideSwiper} from '../../../../../../design-system/organisms/ViewerSlideSwiper/ViewerSlideSwiper';
import {ViewDirection} from '../../../../../../graphql/generated/types';
import {
  CONTENTS_MOBILE_MAX_WIDTH,
  CONTENTS_PC_MARGIN_TOP_BOTTOM,
  CONTENTS_PC_MAX_HEIGHT,
  CONTENTS_PC_MAX_WIDTH,
  CONTENTS_PC_TWO_PAGE_MAX_WIDTH,
  SLIDE_TO_ZERO_IMAGE_VIEWER_EVENT_NAME,
} from '../constant/viewerConstant';
import {useViewerMenuHandler} from '../hook/useViewerMenuHandler';
import {setCurrentPage, setIsViewerEnd} from '../state/imageViewerSlice';
import {setIsViewerMenuExposed} from '../state/viewerMenuSlice';
import {ViewerProgressContainer} from './ViewerProgressContainer';

export function PageImageViewerContainer({
  children,
  productId,
  seriesId,
}: {
  seriesId: number;
  productId: number;
  children?: ReactNode;
}) {
  const {isMobile} = requestContext.requestInfo.display;
  const windowInnerHeight = useWindowInnerHeight();
  const [swiper, setSwiper] = useState<Swiper>();
  const dispatch = useTypedDispatch();
  const {shouldUseNonstopWatching} = useViewerMenuHandler({
    isMobile,
  });

  const {
    downloadData,
    viewDirection,
    currentPage,
    totalPage,
    isTwoPageLayout,
    isBlackSpaceColor,
  } = useTypedSelector(state => ({
    downloadData: state.imageViewer.viewData[productId]?.downloadData,
    viewDirection:
      state.imageViewer.viewData[productId]?.downloadData?.viewDirection,
    currentPage: state.imageViewer.viewData[productId]?.currentPage || 1,
    totalPage: state.imageViewer.viewData[productId]?.totalPage || 1,
    isTwoPageLayout: state.imageViewer.settings.isTwoPageLayout,
    isBlackSpaceColor: state.imageViewer.settings.spaceColor === 'Black',
  }));

  // 처음으로 가기 custom event. swiper를 밖에서 조작하기 위해 custom으로 이벤트를 붙혀주었습니다.
  useEffect(() => {
    function slideToZero() {
      swiper?.slideTo(0);
    }
    window.addEventListener(SLIDE_TO_ZERO_IMAGE_VIEWER_EVENT_NAME, slideToZero);
    return () =>
      window.removeEventListener(
        SLIDE_TO_ZERO_IMAGE_VIEWER_EVENT_NAME,
        slideToZero
      );
  }, [swiper]);

  // currentPage가 어떤 이유에서든 변경 되었을 때 slideTo로 해당 페이지로 이동합니다.
  useEffect(() => {
    if (!downloadData || !swiper) {
      return;
    }
    // 현재 인덱스와 같다면 이동할 필요가 없습니다.
    if (swiper.activeIndex === currentPage - 1) {
      return;
    }
    swiper.slideTo(currentPage - 1, 0);
  }, [
    currentPage,
    dispatch,
    downloadData,
    isTwoPageLayout,
    productId,
    swiper,
    totalPage,
  ]);

  // 슬라이드가 변경되었을 때 실행됩니다.
  const onSlideChange = useCallback(
    (swiper: Swiper) => {
      if (!downloadData) {
        return;
      }
      // 현재 인덱스와 같다면 변경할 필요가 없습니다.
      if (swiper.activeIndex === currentPage - 1) {
        return;
      }

      const totalPageIndex = totalPage - 1;
      // 슬라이드가 변경되면 해당 슬라이드가 뷰어 엔드인지 확인하고 뷰어 엔드라면 is_done을 세팅해줍니다.
      const _isViewerEnd = isTwoPageLayout
        ? swiper.activeIndex >= totalPageIndex
        : swiper.activeIndex > totalPageIndex;

      dispatch(setCurrentPage({productId, page: swiper.activeIndex + 1}));
      dispatch(setIsViewerEnd({productId, isViewerEnd: _isViewerEnd}));

      //뷰어 메뉴 숨김
      dispatch(setIsViewerMenuExposed({isExposed: false}));
    },
    [currentPage, dispatch, downloadData, isTwoPageLayout, productId, totalPage]
  );

  if (!downloadData) {
    return null;
  }

  return (
    <div
      className="h-full w-full"
      style={{
        marginTop: isMobile ? 0 : CONTENTS_PC_MARGIN_TOP_BOTTOM,
        marginBottom: isMobile ? 0 : CONTENTS_PC_MARGIN_TOP_BOTTOM,
        height: isMobile
          ? windowInnerHeight
          : windowInnerHeight - CONTENTS_PC_MARGIN_TOP_BOTTOM * 2,
      }}
    >
      <ViewerSlideSwiper
        isMobile={isMobile}
        isReverseDir={viewDirection === ViewDirection.Backward}
        isTwoPageLayout={isTwoPageLayout}
        isBlackSpaceColor={isBlackSpaceColor}
        onSwiper={setSwiper}
        onSlideChange={onSlideChange}
        className="h-full w-full"
        style={
          !isMobile
            ? {
                maxWidth: isTwoPageLayout
                  ? CONTENTS_PC_TWO_PAGE_MAX_WIDTH
                  : CONTENTS_PC_MAX_WIDTH,
              }
            : undefined
        }
      >
        {downloadData.files.map((file, index) => {
          const url = file.secureUrl;

          const preloadIndexStart = currentPage - 1; // 두쪽보기와 한쪽보기 모두 보여지는 페이지의 시작은 currentPage - 1
          const preloadIndexEnd = currentPage + 2;

          const width = file.width ?? 0;
          const height = file.height ?? 0;
          const aspectRatio = width !== 0 ? height / width : 0;

          const isTwoPageLayoutInPc = !isMobile && isTwoPageLayout;
          const isForwardDirection = viewDirection === ViewDirection.Forward;
          const isBackwardDirection = viewDirection === ViewDirection.Backward;
          const isEvenIndex = index % 2 === 0;

          return (
            <ViewerSlideSwiper.Slide key={index}>
              <div
                className={twMerge(
                  'flex h-full w-full items-center justify-center overflow-hidden',
                  isTwoPageLayoutInPc &&
                    isForwardDirection &&
                    (isEvenIndex ? 'justify-end' : 'justify-start'),
                  isTwoPageLayoutInPc &&
                    isBackwardDirection &&
                    (isEvenIndex ? 'justify-start' : 'justify-end')
                )}
                style={{
                  maxWidth: isMobile ? CONTENTS_MOBILE_MAX_WIDTH : undefined,
                }}
              >
                <Image
                  className="h-full w-full align-middle"
                  imageClassName={twMerge(
                    isTwoPageLayoutInPc &&
                      isForwardDirection &&
                      (isEvenIndex ? 'object-right' : 'object-left'),
                    isTwoPageLayoutInPc &&
                      isBackwardDirection &&
                      (isEvenIndex ? 'object-left' : 'object-right')
                  )}
                  src={url}
                  objectFit="contain"
                  isLazy={index < preloadIndexStart || index > preloadIndexEnd}
                  forceUseLazyInView={true}
                  showFallbackOnError={true}
                  ratio={aspectRatio}
                  style={{
                    maxWidth: isMobile
                      ? CONTENTS_MOBILE_MAX_WIDTH
                      : CONTENTS_PC_MAX_WIDTH,
                  }}
                  alt=""
                  onSelect={preventDefaultEvent}
                  onDragStart={preventDefaultEvent}
                  onContextMenu={preventDefaultEvent}
                />
              </div>
            </ViewerSlideSwiper.Slide>
          );
        })}
        {!shouldUseNonstopWatching && (
          <ViewerSlideSwiper.Slide key="viewerend">
            <div
              className={twMerge(
                'h-full w-full overflow-y-auto',
                !isMobile && 'flex items-center'
              )}
              style={{
                height: `${
                  isMobile
                    ? windowInnerHeight
                    : windowInnerHeight - CONTENTS_PC_MARGIN_TOP_BOTTOM * 2
                }px`,
                maxWidth: isMobile ? CONTENTS_MOBILE_MAX_WIDTH : undefined,
              }}
            >
              <div
                className={twMerge(
                  'w-full',
                  isMobile
                    ? 'min-h-full bg-theme-bg-surface'
                    : 'flex h-full items-center bg-theme-bg-surface-alt'
                )}
                style={{
                  maxHeight: !isMobile ? CONTENTS_PC_MAX_HEIGHT : undefined,
                }}
              >
                {children}
              </div>
            </div>
          </ViewerSlideSwiper.Slide>
        )}
        {!isMobile &&
          isTwoPageLayout &&
          (!shouldUseNonstopWatching
            ? totalPage % 2 === 0
            : totalPage % 2 !== 0) && (
            <ViewerSlideSwiper.Slide key="dummy">
              <div
                className="h-full w-full"
                style={{
                  height: `${
                    windowInnerHeight - CONTENTS_PC_MARGIN_TOP_BOTTOM * 2
                  }px`,
                }}
              />
            </ViewerSlideSwiper.Slide>
          )}
      </ViewerSlideSwiper>
      <ViewerProgressContainer seriesId={seriesId} productId={productId} />
    </div>
  );
}
